import { HttpEvent, HttpHandlerFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { AuthService } from 'app/core/auth/auth.service';
import { AuthUtils } from 'app/core/auth/auth.utils';
import { environment } from 'environments/environment';
import { Observable, concatMap, tap } from 'rxjs';

/**
 * Intercept
 *
 * @param req
 * @param next
 */
export const authInterceptor = (req: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> => {
  const authService = inject(AuthService);

  let pendingAuthCheck: Observable<boolean>;

  const authCheck = (): Observable<boolean> => {
    // Ensure there's only one auth check being performed and all requests need to queue up behind it
    if (pendingAuthCheck) {
      return pendingAuthCheck;
    }

    return (pendingAuthCheck = authService.check().pipe(
      tap(() => (pendingAuthCheck = null)) // Clear pending auth check
    ));
  };

  // Clone the request object, and add clientId and API version headers
  let newReq = req.clone({
    headers: req.headers.set('clientId', environment.clientId).set('x-api-version', environment.xApiVersion),
  });

  // Response
  if (/\/token|\/refresh$|\/AryzaToken/i.test(newReq.url)) {
    // Just pass the request through for the Token and Refresh endpoints
    return next(newReq);
  } else {
    // First check if token needs to be refreshed, then proceed with original request
    return authCheck().pipe(
      concatMap(() => {
        // Request
        //
        // If the access token didn't expire, add the Authorization header.
        // We won't add the Authorization header if the access token expired.
        // This will force the server to return a "401 Unauthorized" response
        // for the protected API routes which our response interceptor will
        // catch and delete the access token from the local storage while logging
        // the user out from the app.
        if (authService.accessToken && !AuthUtils.isTokenExpired(authService.accessToken)) {
          newReq = newReq.clone({
            headers: newReq.headers.set('Authorization', 'Bearer ' + authService.accessToken),
          });
        }

        return next(newReq);
      })
    );
  }
};
